import { encodeCanvas } from '../shared/imageOutputFormats';
export const PEN_COLORS = ['#000000', '#1E3A8A', '#374151'];
export const FONT_SIZES = [48, 64, 80, 96];
export const BACKGROUND_MODES = ['transparent', 'white'];
export const MAX_TEXT_LENGTH = 60;

export const SIGNATURE_FONTS = [
    { id: 'dancing', family: 'Dancing Script', google: 'Dancing+Script' },
    { id: 'greatVibes', family: 'Great Vibes', google: 'Great+Vibes' },
    { id: 'allura', family: 'Allura', google: 'Allura' },
    { id: 'sacramento', family: 'Sacramento', google: 'Sacramento' },
    { id: 'caveat', family: 'Caveat', google: 'Caveat' },
    { id: 'galada', family: 'Galada', google: 'Galada' },
    { id: 'tiroBangla', family: 'Tiro Bangla', google: 'Tiro+Bangla:ital@1' },
];

const loadedFonts = new Set();

export function isSignatureTextValid(text) {
    return Boolean(text?.trim());
}

export function outputFileName(ext = 'png') {
    return `text-signature.${ext}`;
}

export function getFontById(id) {
    return SIGNATURE_FONTS.find((font) => font.id === id) || SIGNATURE_FONTS[0];
}

export function loadSignatureFont(font) {
    if (!font || loadedFonts.has(font.family)) {
        return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
        const linkId = `signature-font-${font.id}`;
        if (!document.getElementById(linkId)) {
            const link = document.createElement('link');
            link.id = linkId;
            link.rel = 'stylesheet';
            link.href = `https://fonts.googleapis.com/css2?family=${font.google}&display=swap`;
            link.onerror = () => reject(new Error('font-load-failed'));
            document.head.appendChild(link);
        }

        const tryLoad = () => {
            document.fonts.load(`48px "${font.family}"`)
                .then(() => {
                    loadedFonts.add(font.family);
                    resolve();
                })
                .catch(reject);
        };

        const existing = document.getElementById(linkId);
        if (existing?.sheet) {
            tryLoad();
        } else if (existing) {
            existing.addEventListener('load', tryLoad, { once: true });
            existing.addEventListener('error', () => reject(new Error('font-load-failed')), { once: true });
        } else {
            tryLoad();
        }
    });
}

function buildCanvasFont(font, fontSize) {
    const style = font.id === 'tiroBangla' ? 'italic ' : '';
    return `${style}${fontSize}px "${font.family}"`;
}

/** Render signature text to a PNG blob. */
export async function renderSignaturePng(text, {
    font,
    fontSize,
    color,
    background = 'transparent',
    format = 'png',
}) {
    const trimmed = text.trim();
    if (!trimmed) {
        throw new Error('empty');
    }

    await loadSignatureFont(font);

    const measureCanvas = document.createElement('canvas');
    const measureCtx = measureCanvas.getContext('2d');
    measureCtx.font = buildCanvasFont(font, fontSize);
    const metrics = measureCtx.measureText(trimmed);

    const padding = Math.round(fontSize * 0.35);
    const width = Math.ceil(metrics.width + padding * 2);
    const height = Math.ceil(fontSize * 1.5 + padding);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    if (background === 'white') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
    }

    ctx.font = buildCanvasFont(font, fontSize);
    ctx.fillStyle = color;
    ctx.textBaseline = 'middle';
    ctx.fillText(trimmed, padding, height / 2);

    // JPG is laid onto white inside encodeCanvas; PNG and WebP keep the alpha.
    return (await encodeCanvas(canvas, format)).blob;
}
